import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoToSDKVideo } from '@wix/sdk-runtime/transformations/video';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateDraftPostsOptions,
  BulkCreateDraftPostsResponse,
  BulkDeleteDraftPostsOptions,
  BulkDeleteDraftPostsResponse,
  BulkUpdateDraftPostsOptions,
  BulkUpdateDraftPostsResponse,
  CreateDraftPostOptions,
  CreateDraftPostResponse,
  DeleteDraftPostOptions,
  DraftCreatedEnvelope,
  DraftDeletedEnvelope,
  DraftPost,
  DraftPostsQueryBuilder,
  DraftUpdatedEnvelope,
  GetDeletedDraftPostResponse,
  GetDraftPostOptions,
  GetDraftPostResponse,
  ListDeletedDraftPostsOptions,
  ListDeletedDraftPostsResponse,
  ListDraftPostsOptions,
  ListDraftPostsResponse,
  PublishDraftPostResponse,
  QueryDraftPostsOptions,
  RestoreFromTrashBinResponse,
  UpdateDraftPost,
  UpdateDraftPostOptions,
  UpdateDraftPostResponse,
  bulkCreateDraftPosts as universalBulkCreateDraftPosts,
  bulkDeleteDraftPosts as universalBulkDeleteDraftPosts,
  bulkUpdateDraftPosts as universalBulkUpdateDraftPosts,
  createDraftPost as universalCreateDraftPost,
  deleteDraftPost as universalDeleteDraftPost,
  getDeletedDraftPost as universalGetDeletedDraftPost,
  getDraftPost as universalGetDraftPost,
  listDeletedDraftPosts as universalListDeletedDraftPosts,
  listDraftPosts as universalListDraftPosts,
  publishDraftPost as universalPublishDraftPost,
  queryDraftPosts as universalQueryDraftPosts,
  removeFromTrashBin as universalRemoveFromTrashBin,
  restoreFromTrashBin as universalRestoreFromTrashBin,
  updateDraftPost as universalUpdateDraftPost,
} from './blog-v3-draft-draft-posts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/blog' };

type DraftNonNullablePaths =
  | `_id`
  | `title`
  | `categoryIds`
  | `hashtags`
  | `minutesToRead`
  | `tagIds`
  | `relatedPostIds`
  | `pricingPlanIds`
  | `status`
  | `moderationDetails.submittedBy`
  | `moderationDetails.status`
  | `hasUnpublishedChanges`
  | `seoData.tags`
  | `seoData.tags.${number}.type`
  | `seoData.tags.${number}.children`
  | `seoData.tags.${number}.custom`
  | `seoData.tags.${number}.disabled`
  | `seoData.settings.preventAutoRedirect`
  | `seoData.settings.keywords`
  | `seoData.settings.keywords.${number}.term`
  | `seoData.settings.keywords.${number}.isMain`
  | `media.embedMedia.thumbnail.url`
  | `media.embedMedia.thumbnail.width`
  | `media.embedMedia.thumbnail.height`
  | `media.embedMedia.video.url`
  | `media.embedMedia.video.width`
  | `media.embedMedia.video.height`
  | `media.displayed`
  | `media.custom`;

export function createDraftPost(
  httpClient: HttpClient
): CreateDraftPostSignature {
  return (
    draftPost: NonNullablePaths<DraftPost, `title`>,
    options?: CreateDraftPostOptions
  ) =>
    universalCreateDraftPost(
      draftPost,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDraftPostSignature {
  /**
   * Creates a draft post.
   *
   * For 3rd-party apps, `memberId` is a required field.
   *
   * In `categoryIds`, only categories that already exist will be added to the draft post. Including
   * `categoryIds` that do not exist will not return an error, but they will not be added to the `categoryIds` array.
   * If some of the categories exist, those will be added while those that do not exist will not be added to `categoryIds`.
   * You can check which categories are available with
   * List Categories ([SDK](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/list-categories) | [REST](https://dev.wix.com/docs/rest/business-solutions/blog/category/list-categories)).
   * @param - Draft post to create.
   * @param - Options for creating a draft post.
   */
  (
    draftPost: NonNullablePaths<DraftPost, `title`>,
    options?: CreateDraftPostOptions
  ): Promise<
    NonNullablePaths<
      CreateDraftPostResponse,
      { [P in DraftNonNullablePaths]: `draftPost.${P}` }[DraftNonNullablePaths]
    >
  >;
}

export function bulkCreateDraftPosts(
  httpClient: HttpClient
): BulkCreateDraftPostsSignature {
  return (
    draftPosts: NonNullablePaths<DraftPost, `title`>[],
    options?: BulkCreateDraftPostsOptions
  ) =>
    universalBulkCreateDraftPosts(
      draftPosts,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateDraftPostsSignature {
  /**
   * Creates multiple draft posts.
   * @param - Draft posts to create.
   * @param - Options for creating multiple draft posts.
   */
  (
    draftPosts: NonNullablePaths<DraftPost, `title`>[],
    options?: BulkCreateDraftPostsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateDraftPostsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | {
          [P in DraftNonNullablePaths]: `results.${number}.item.${P}`;
        }[DraftNonNullablePaths]
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`
    >
  >;
}

export function bulkUpdateDraftPosts(
  httpClient: HttpClient
): BulkUpdateDraftPostsSignature {
  return (
    options?: NonNullablePaths<
      BulkUpdateDraftPostsOptions,
      `draftPosts.${number}.draftPost` | `draftPosts.${number}.draftPost._id`
    >
  ) =>
    universalBulkUpdateDraftPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDraftPostsSignature {
  /**
   * Updates multiple draft posts.
   * @param - Options for updating multiple draft posts.
   */
  (
    options?: NonNullablePaths<
      BulkUpdateDraftPostsOptions,
      `draftPosts.${number}.draftPost` | `draftPosts.${number}.draftPost._id`
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateDraftPostsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | {
          [P in DraftNonNullablePaths]: `results.${number}.item.${P}`;
        }[DraftNonNullablePaths]
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`
    >
  >;
}

export function listDeletedDraftPosts(
  httpClient: HttpClient
): ListDeletedDraftPostsSignature {
  return (options?: ListDeletedDraftPostsOptions) =>
    universalListDeletedDraftPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeletedDraftPostsSignature {
  /**
   * Retrieves a list of up to 100 deleted draft posts.
   *
   * List Draft Posts runs with these defaults, which you can override:
   * - `editedDate` is sorted in descending order. In this case,`editedDate` implies the date the post was deleted.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   * @param - Options for listing deleted draft posts.
   */
  (options?: ListDeletedDraftPostsOptions): Promise<
    NonNullablePaths<
      ListDeletedDraftPostsResponse,
      | {
          [P in DraftNonNullablePaths]: `draftPosts.${number}.${P}`;
        }[DraftNonNullablePaths]
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`
    >
  >;
}

export function getDraftPost(httpClient: HttpClient): GetDraftPostSignature {
  return (draftPostId: string, options?: GetDraftPostOptions) =>
    universalGetDraftPost(
      draftPostId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDraftPostSignature {
  /**
   * Retrieves a draft post by the provided ID.
   * @param - Draft post ID.
   * @param - Options for getting a draft post.
   */
  (draftPostId: string, options?: GetDraftPostOptions): Promise<
    NonNullablePaths<
      GetDraftPostResponse,
      { [P in DraftNonNullablePaths]: `draftPost.${P}` }[DraftNonNullablePaths]
    >
  >;
}

export function updateDraftPost(
  httpClient: HttpClient
): UpdateDraftPostSignature {
  return (
    _id: string,
    draftPost: UpdateDraftPost,
    options?: UpdateDraftPostOptions
  ) =>
    universalUpdateDraftPost(
      _id,
      draftPost,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDraftPostSignature {
  /**
   * Updates a draft post.
   * @param - Draft post ID.
   * @param - Options for updating a draft post.
   * @param - Draft Post info.
   */
  (
    _id: string,
    draftPost: UpdateDraftPost,
    options?: UpdateDraftPostOptions
  ): Promise<
    NonNullablePaths<
      UpdateDraftPostResponse,
      { [P in DraftNonNullablePaths]: `draftPost.${P}` }[DraftNonNullablePaths]
    >
  >;
}

export function deleteDraftPost(
  httpClient: HttpClient
): DeleteDraftPostSignature {
  return (draftPostId: string, options?: DeleteDraftPostOptions) =>
    universalDeleteDraftPost(
      draftPostId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDraftPostSignature {
  /**
   * Moves a draft post to the trash bin.
   * A published post can also be deleted by the `post.id`. See the Posts API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/blog/posts/) | [REST](https://dev.wix.com/docs/rest/business-solutions/blog/posts-stats/post-object)) for more information about posts.
   *
   * To permanently delete a post bypassing the trash bin, set the `permanent` field value to `true`. The post can't be restored after this.
   * @param - Draft post ID.
   * @param - Options for deleting a draft post.
   */
  (draftPostId: string, options?: DeleteDraftPostOptions): Promise<void>;
}

export function removeFromTrashBin(
  httpClient: HttpClient
): RemoveFromTrashBinSignature {
  return (draftPostId: string) =>
    universalRemoveFromTrashBin(
      draftPostId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveFromTrashBinSignature {
  /**
   * Permanently deletes a draft post that is currently in the trash bin.
   *
   * This action is permanent and can't be reversed.
   * @param - Draft post ID.
   */
  (draftPostId: string): Promise<void>;
}

export function bulkDeleteDraftPosts(
  httpClient: HttpClient
): BulkDeleteDraftPostsSignature {
  return (postIds: string[], options?: BulkDeleteDraftPostsOptions) =>
    universalBulkDeleteDraftPosts(
      postIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteDraftPostsSignature {
  /**
   * Deletes multiple draft posts.
   * @param - Post IDs.
   * @param - Options for deleting multiple draft posts.
   */
  (postIds: string[], options?: BulkDeleteDraftPostsOptions): Promise<
    NonNullablePaths<
      BulkDeleteDraftPostsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | {
          [P in DraftNonNullablePaths]: `results.${number}.item.${P}`;
        }[DraftNonNullablePaths]
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`
    >
  >;
}

export function listDraftPosts(
  httpClient: HttpClient
): ListDraftPostsSignature {
  return (options?: ListDraftPostsOptions) =>
    universalListDraftPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDraftPostsSignature {
  /**
   * Retrieves a list of up to 100 draft posts per request.
   *
   * List Draft Posts runs with these defaults, which you can override:
   * - `editedDate` is sorted in descending order.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   * @param - Options for listing multiple draft posts.
   */
  (options?: ListDraftPostsOptions): Promise<
    NonNullablePaths<
      ListDraftPostsResponse,
      | {
          [P in DraftNonNullablePaths]: `draftPosts.${number}.${P}`;
        }[DraftNonNullablePaths]
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`
    >
  >;
}

export function getDeletedDraftPost(
  httpClient: HttpClient
): GetDeletedDraftPostSignature {
  return (draftPostId: string) =>
    universalGetDeletedDraftPost(
      draftPostId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeletedDraftPostSignature {
  /**
   * Retrieves a deleted draft post from the trash bin by the provided ID.
   * @param - Draft post ID.
   */
  (draftPostId: string): Promise<
    NonNullablePaths<
      GetDeletedDraftPostResponse,
      { [P in DraftNonNullablePaths]: `draftPost.${P}` }[DraftNonNullablePaths]
    >
  >;
}

export function restoreFromTrashBin(
  httpClient: HttpClient
): RestoreFromTrashBinSignature {
  return (draftPostId: string) =>
    universalRestoreFromTrashBin(
      draftPostId,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreFromTrashBinSignature {
  /**
   * Restores a deleted draft post from the trash bin by the provided ID.
   * @param - Draft post ID.
   */
  (draftPostId: string): Promise<
    NonNullablePaths<
      RestoreFromTrashBinResponse,
      { [P in DraftNonNullablePaths]: `draftPost.${P}` }[DraftNonNullablePaths]
    >
  >;
}

export function queryDraftPosts(
  httpClient: HttpClient
): QueryDraftPostsSignature {
  return (options?: QueryDraftPostsOptions) =>
    universalQueryDraftPosts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDraftPostsSignature {
  /**
   * Creates a query to retrieve a list of up to 100 draft posts.
   *
   *
   * The `queryDraftPosts()` function builds a query to retrieve a list of up to 100 draft posts, and returns a `DraftPostsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `DraftPostsQueryBuilder` functions onto the query. `DraftPostsQueryBuilder` functions enable you to sort, filter, and control the results that `queryDraftPosts()` returns.
   *
   * `queryDraftPosts()` runs with these `DraftPostsQueryBuilder` defaults that can be overridden:
   * - `limit(50)`
   * - `ascending('_id')`
   *
   * The following `DraftPostsQueryBuilder` functions are supported for `queryDraftPosts()`. For a full description of the Draft Posts object, see the object returned for the `items` property in `DraftPostsQueryResult`.
   * @param - Options for querying draft posts.
   */
  (options?: QueryDraftPostsOptions): DraftPostsQueryBuilder;
}

export function publishDraftPost(
  httpClient: HttpClient
): PublishDraftPostSignature {
  return (draftPostId: string) =>
    universalPublishDraftPost(
      draftPostId,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishDraftPostSignature {
  /**
   * Publishes a draft post by ID. This creates a new post ([SDK](https://dev.wix.com/docs/sdk/backend-modules/blog/posts/) | [REST](https://dev.wix.com/docs/rest/business-solutions/blog/posts-stats/post-object))
   * with the data from the draft post.
   *
   * If the draft post was already published, the published post will be updated with the latest values
   * from the draft post.
   * @param - Draft post ID.
   */
  (draftPostId: string): Promise<
    NonNullablePaths<PublishDraftPostResponse, `postId`>
  >;
}

export const onDraftCreated = EventDefinition(
  'wix.blog.v3.draft_created',
  true,
  (event: DraftCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.heroImage' },
            { path: 'entity.coverMedia.image' },
            { path: 'entity.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.editedDate' },
            { path: 'entity.scheduledPublishDate' },
            { path: 'entity.firstPublishedDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'entity.moderationDetails.submittedDate' },
            { path: 'entity.moderationDetails.moderationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTVideoToSDKVideo,
          paths: [{ path: 'entity.coverMedia.video' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.media.wixMedia.videoV2' }],
        },
      ])
    )
)<DraftCreatedEnvelope>();
export const onDraftDeleted = EventDefinition(
  'wix.blog.v3.draft_deleted',
  true,
  (event: DraftDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.heroImage' },
            { path: 'entity.coverMedia.image' },
            { path: 'entity.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.editedDate' },
            { path: 'entity.scheduledPublishDate' },
            { path: 'entity.firstPublishedDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'entity.moderationDetails.submittedDate' },
            { path: 'entity.moderationDetails.moderationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTVideoToSDKVideo,
          paths: [{ path: 'entity.coverMedia.video' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.media.wixMedia.videoV2' }],
        },
      ])
    )
)<DraftDeletedEnvelope>();
export const onDraftUpdated = EventDefinition(
  'wix.blog.v3.draft_updated',
  true,
  (event: DraftUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.heroImage' },
            { path: 'entity.coverMedia.image' },
            { path: 'entity.media.wixMedia.image' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.editedDate' },
            { path: 'entity.scheduledPublishDate' },
            { path: 'entity.firstPublishedDate' },
            { path: 'entity.createdDate' },
            { path: 'entity.richContent.metadata.createdTimestamp' },
            { path: 'entity.richContent.metadata.updatedTimestamp' },
            { path: 'entity.moderationDetails.submittedDate' },
            { path: 'entity.moderationDetails.moderationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTVideoToSDKVideo,
          paths: [{ path: 'entity.coverMedia.video' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.richContent.nodes.galleryData.options.item.ratio' },
            { path: 'entity.richContent.nodes.imageData.image.duration' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lat' },
            { path: 'entity.richContent.nodes.mapData.mapSettings.lng' },
            { path: 'entity.richContent.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.richContent.nodes.appEmbedData.image.duration' },
            { path: 'entity.richContent.nodes.videoData.video.duration' },
            { path: 'entity.richContent.nodes.videoData.thumbnail.duration' },
            { path: 'entity.richContent.nodes.audioData.audio.duration' },
            { path: 'entity.richContent.nodes.audioData.coverImage.duration' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.media.wixMedia.videoV2' }],
        },
      ])
    )
)<DraftUpdatedEnvelope>();

export {
  Action,
  ActionEvent,
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  ApproveDraftPostRequest,
  ApproveDraftPostResponse,
  AspectRatio,
  AudioData,
  Background,
  BackgroundBackgroundOneOf,
  BackgroundType,
  BaseEventMetadata,
  BlockquoteData,
  BlogPaging,
  BookingData,
  Border,
  BorderColors,
  BulkActionMetadata,
  BulkCreateDraftPostsOptions,
  BulkCreateDraftPostsRequest,
  BulkCreateDraftPostsResponse,
  BulkDeleteDraftPostsOptions,
  BulkDeleteDraftPostsRequest,
  BulkDeleteDraftPostsResponse,
  BulkDraftPostResult,
  BulkRejectDraftPostRequest,
  BulkRejectDraftPostResponse,
  BulkRevertToUnpublishedRequest,
  BulkRevertToUnpublishedResponse,
  BulkUpdateDraftPostLanguageRequest,
  BulkUpdateDraftPostLanguageResponse,
  BulkUpdateDraftPostsOptions,
  BulkUpdateDraftPostsRequest,
  BulkUpdateDraftPostsResponse,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardStyles,
  CardStylesType,
  Category,
  CategoryTranslation,
  CellStyle,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  CoverMedia,
  CoverMediaMediaOneOf,
  CreateDraftPostOptions,
  CreateDraftPostRequest,
  CreateDraftPostResponse,
  Crop,
  CursorPaging,
  Cursors,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteDraftPostOptions,
  DeleteDraftPostRequest,
  DeleteDraftPostResponse,
  Design,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftCategoriesUpdated,
  DraftCreatedEnvelope,
  DraftDeletedEnvelope,
  DraftPost,
  DraftPostOwnerChanged,
  DraftPostTranslation,
  DraftPostsQueryBuilder,
  DraftPostsQueryResult,
  DraftTagsUpdated,
  DraftUpdatedEnvelope,
  EmbedData,
  EmbedMedia,
  EmbedThumbnail,
  EmbedVideo,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventData,
  EventMetadata,
  Field,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FocalPoint,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetDeletedDraftPostRequest,
  GetDeletedDraftPostResponse,
  GetDraftPostOptions,
  GetDraftPostRequest,
  GetDraftPostResponse,
  GetDraftPostTotalsRequest,
  GetDraftPostTotalsResponse,
  GetDraftPostsSort,
  GetPostAmountsByLanguageRequest,
  GetPostAmountsByLanguageResponse,
  Gradient,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImageStyles,
  InitialDraftPostsCopied,
  InitialExpandedItems,
  IsDraftPostAutoTranslatableRequest,
  IsDraftPostAutoTranslatableResponse,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  Keyword,
  Layout,
  LayoutCellData,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListDeletedDraftPostsOptions,
  ListDeletedDraftPostsRequest,
  ListDeletedDraftPostsResponse,
  ListDraftPostsOptions,
  ListDraftPostsRequest,
  ListDraftPostsResponse,
  ListValue,
  MapData,
  MapSettings,
  MapType,
  MarkPostAsInModerationRequest,
  MarkPostAsInModerationResponse,
  MaskedDraftPosts,
  Media,
  MediaMediaOneOf,
  MentionData,
  MessageEnvelope,
  MetaData,
  Metadata,
  ModerationDetails,
  ModerationStatusStatus,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Paging,
  PagingMetadataV2,
  ParagraphData,
  Permissions,
  Placement,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  PostAmountByLanguage,
  PublishDraftPostRequest,
  PublishDraftPostResponse,
  QueryDraftPostsOptions,
  QueryDraftPostsRequest,
  QueryDraftPostsResponse,
  RejectDraftPostRequest,
  RejectDraftPostResponse,
  Rel,
  RemoveFromTrashBinRequest,
  RemoveFromTrashBinResponse,
  Resizing,
  RestoreFromTrashBinRequest,
  RestoreFromTrashBinResponse,
  RestoreInfo,
  RevertToUnpublishedRequest,
  RevertToUnpublishedResponse,
  RibbonStyles,
  RichContent,
  SeoSchema,
  Settings,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  Status,
  Styles,
  StylesBorder,
  StylesPosition,
  TableCellData,
  TableData,
  Tag,
  Target,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TotalDraftPosts,
  TotalDraftPostsGroupingField,
  TranslateCategoryRequest,
  TranslateCategoryResponse,
  TranslateDraftRequest,
  TranslateDraftResponse,
  Type,
  UnpublishPostRequest,
  UnpublishPostResponse,
  UpdateDraftPost,
  UpdateDraftPostContentRequest,
  UpdateDraftPostContentResponse,
  UpdateDraftPostLanguageRequest,
  UpdateDraftPostLanguageResponse,
  UpdateDraftPostOptions,
  UpdateDraftPostRequest,
  UpdateDraftPostResponse,
  V1Media,
  VerticalAlignment,
  Video,
  VideoData,
  VideoResolution,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  WixMedia,
} from './blog-v3-draft-draft-posts.universal.js';
